import { action, API, controller } from "api";
import { useAppContext } from "context";
import { InputDate } from "controls";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { endDayFor, titleFor } from "../fiscalYearDates";

/**
 * The fiscal year's dates. The SERVER decides them (2026-10-01): a company's next year starts the
 * day after its previous year ends and runs exactly 12 months, so both dates are shown locked. Only a
 * company's FIRST year is the person's to shape (2026-10-04): any start up to today, and any end after
 * it - past or future, any length - because company law treats a first financial year that way and a
 * fresh tenant needs one year that already covers its history. Until 2026-10-01 the form took the
 * start from the last closed year of ANY company and converted it in the browser's time zone - which
 * is how two years came to overlap by six hours.
 */
const InputFyDate = ({ values, setValues, errors, handleInpDate }) => {
  const { t } = useTranslation();
  const { selCompany } = useAppContext();
  const [datesLocked, setDatesLocked] = useState(false);
  const [maxStartDay, setMaxStartDay] = useState(dayjs());
  // Once the person picks an end, a later change of start must not silently replace it.
  const [endTouched, setEndTouched] = useState(false);

  const firstYear = !values.id && !datesLocked;

  // Defaults for a NEW year of the company being viewed.
  useEffect(() => {
    if (values.id || !selCompany) return;
    API(controller.FiscalYear + action.NewYearDefaults).getById(selCompany, (res) => {
      if (res?.status !== 200 || !res?.result) return;
      const d = res.result;
      // `endLocked` from a current server; an older one only sends startLocked, which is the same fact.
      setDatesLocked(!!(d.endLocked ?? d.startLocked));
      setMaxStartDay(dayjs(d.maxStartDay));
      setValues((prev) => ({
        ...prev,
        startDate: dayjs(d.startDay),
        endDate: dayjs(d.endDay),
        title: d.title,
      }));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selCompany, values.id]);

  // A first year's start may be picked. The end follows it to 12 months only until the person has
  // chosen one, and an end that would now fall on or before the start is moved on again.
  useEffect(() => {
    if (!firstYear || !values.startDate) return;
    setValues((prev) => {
      const start = dayjs(prev.startDate);
      const keepEnd = endTouched && prev.endDate && dayjs(prev.endDate).isAfter(start, "day");
      const end = keepEnd ? dayjs(prev.endDate) : endDayFor(start);
      return { ...prev, endDate: end, title: titleFor(start, end) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.startDate]);

  const pickEnd = (date) => {
    if (!date) return;
    setEndTouched(true);
    setValues((prev) => ({
      ...prev,
      endDate: dayjs(date),
      title: titleFor(prev.startDate, date),
    }));
  };

  return (
    <div>
      <div className="flex gap-3">
        <InputDate
          size="small"
          variant="outlined"
          label={t("startDate")}
          required
          name="startDate"
          error={errors.startDate}
          value={values.startDate}
          disabled={!firstYear}
          views={["day"]}
          openTo="day"
          maxDate={maxStartDay}
          onChange={(date) =>
            handleInpDate(
              "startDate",
              date ? dayjs(date).format("DD/MMM/YYYY") : ""
            )
          }
        />
        <InputDate
          size="small"
          variant="outlined"
          label={t("endDate")}
          required
          name="endDate"
          error={errors.endDate}
          value={values.endDate}
          disabled={!firstYear}
          views={["day"]}
          openTo="day"
          // A year ends after it begins; no upper limit for a first year.
          minDate={values.startDate ? dayjs(values.startDate).add(1, "day") : undefined}
          onChange={pickEnd}
        />
      </div>
      {firstYear && (
        <span className="mt-1 block text-[11px] leading-[1.4] text-gray-500">
          {t(
            "firstFiscalYearHint",
            "Your first fiscal year can be any length. Every year after it is 12 months."
          )}
        </span>
      )}
    </div>
  );
};

export default InputFyDate;
